import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { SealCheckIcon as SealCheck, CircleNotchIcon as Spinner } from "@phosphor-icons/react";
import { getFindingsBusinessRules } from "@/lib/api";
import ValidationBadge from "@/components/validation/ValidationBadge";
import { normalizeComparableObject } from "@/lib/compareSelection";
import { CompareSelectButton } from "@/components/compare/CompareSelectButton";

function Rollup({ title, rows, keyName, testid }) {
  if (!rows?.length) return null;
  return (
    <div data-testid={testid}>
      <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] mb-1.5">{title}</div>
      <ul className="space-y-1">
        {rows.slice(0, 8).map((r) => (
          <li key={r[keyName]} className="flex items-baseline justify-between gap-2 text-xs">
            <span className="font-mono font-semibold">{r[keyName]}</span>
            <span className="ml-mono text-[10px] text-[var(--ml-text-secondary)] whitespace-nowrap">
              {r.rule_count} rule{r.rule_count === 1 ? "" : "s"} · {r.workspace_count} eng.
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Knowledge Base: business rules reusable across engagements — validated only by default (Iter 40). */
export default function ValidatedRulesPanel({ compareSelection }) {
  const [includeUnvalidated, setIncludeUnvalidated] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    getFindingsBusinessRules({ includeUnvalidated, limit: 100 })
      .then((d) => alive && setData(d))
      .catch((e) => alive && toast.error(`Couldn't load business rules: ${e?.response?.data?.detail || e.message}`))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [includeUnvalidated]);

  const totals = data?.totals || {};
  const rows = data?.rows || [];
  return (
    <div className="ml-card p-5" data-testid="validated-rules-panel">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <div className="flex items-center gap-1.5 ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] mb-1">
            <SealCheck size={11} weight="bold" /> Reusable business rules
          </div>
          <h2 className="text-lg font-semibold">Validated knowledge across engagements</h2>
          <p className="text-xs text-[var(--ml-text-secondary)] mt-1 max-w-xl">
            Only consultant-validated rules are reused by default. Parser inference stays out of the knowledge base
            until someone signs it off; rejected rules are never reused.
          </p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <div className="flex items-center gap-2 ml-mono text-[10px] uppercase tracking-widest" data-testid="kb-rule-totals">
            <span style={{ color: "#0e6027" }}>{totals.validated || 0} validated</span>
            <span style={{ color: "#6929c4" }}>{totals.proposed || 0} inference</span>
            <span className="text-[var(--ml-text-secondary)]">{totals.rejected || 0} rejected</span>
          </div>
          <label className="inline-flex items-center gap-2 text-xs cursor-pointer select-none">
            <input
              type="checkbox"
              checked={includeUnvalidated}
              onChange={(e) => setIncludeUnvalidated(e.target.checked)}
              data-testid="kb-include-unvalidated"
            />
            Include unvalidated inference
          </label>
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-[var(--ml-text-secondary)] inline-flex items-center gap-2" data-testid="kb-rules-loading">
          <Spinner size={14} className="animate-spin" /> Loading rules…
        </div>
      ) : rows.length === 0 ? (
        <div className="text-sm text-[var(--ml-text-secondary)]" data-testid="kb-rules-empty">
          {includeUnvalidated
            ? "No business rules extracted in your engagements yet."
            : `No validated business rules yet${totals.proposed ? ` — ${totals.proposed} inferred rule${totals.proposed === 1 ? "" : "s"} await review in their workspaces` : ""}. Validate rules on a workspace's Business Rules page to build reusable knowledge.`}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 overflow-x-auto">
            <table className="w-full text-sm" data-testid="kb-rules-table">
              <thead className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] bg-[var(--ml-surface)]/50">
                <tr>
                  <th className="px-2 py-2 text-left">Engagement</th>
                  <th className="px-2 py-2 text-left">Program</th>
                  <th className="px-2 py-2 text-left">Condition</th>
                  <th className="px-2 py-2 text-left">Fields</th>
                  <th className="px-2 py-2 text-left">Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-b border-[var(--ml-border)] align-top" data-testid={`kb-rule-${r.id}`} data-state={r.validation_state}>
                    <td className="px-2 py-2 text-xs">
                      <Link to={`/workspaces/${r.workspace_id}/business-rules`} className="text-[var(--ml-primary)] hover:underline">
                        {r.workspace_name}
                      </Link>
                      <div className="ml-mono text-[10px] text-[var(--ml-text-secondary)]">{r.client_name}</div>
                    </td>
                    <td className="px-2 py-2 ml-mono text-xs font-semibold whitespace-nowrap">
                      {r.program_name}<span className="text-[var(--ml-text-secondary)] font-normal"> :{r.line_number}</span>
                      {compareSelection?.active && (
                        <div className="mt-1">
                          <CompareSelectButton obj={normalizeComparableObject(r, { entity: "rule-program" })} selection={compareSelection} compact />
                        </div>
                      )}
                    </td>
                    <td className="px-2 py-2 ml-mono text-xs whitespace-pre-wrap break-words max-w-[360px]">
                      <span className="uppercase text-[10px] text-[var(--ml-text-secondary)] mr-1">{r.rule_kind}</span>
                      {r.condition_text || <em className="text-[var(--ml-text-secondary)]">(no condition)</em>}
                      {r.validation_note && <div className="text-[11px] text-[var(--ml-text-secondary)] mt-0.5">{r.validation_note}</div>}
                    </td>
                    <td className="px-2 py-2 ml-mono text-[10px]">{(r.referenced_fields || []).join(", ") || "—"}</td>
                    <td className="px-2 py-2"><ValidationBadge state={r.validation_state} row={r} testid={`kb-rule-state-${r.id}`} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="space-y-4">
            <Rollup title="Fields governed by rules" rows={data.by_field} keyName="field" testid="kb-rules-by-field" />
            <Rollup title="Files governed by rules" rows={data.by_file} keyName="file" testid="kb-rules-by-file" />
          </div>
        </div>
      )}
    </div>
  );
}
